import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BarChart3, TrendingUp, Award, Clock, CheckCircle2, XCircle, Users, FileText, Zap } from 'lucide-react';
import { adminApi, schoolsApi } from '../../lib/api';
import { toast } from 'sonner';

export const ExamDashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSchool, setSelectedSchool] = useState('');
  const [schools, setSchools] = useState([]);

  useEffect(() => {
    fetchData();
    if (user?.role === 'super_admin') {
      schoolsApi.getAll().then(setSchools).catch(console.error);
    }
  }, [user]);

  const fetchData = async (schoolId = null) => {
    setLoading(true);
    try {
      const result = await adminApi.getExamDashboard(schoolId);
      setData(result);
    } catch (error) {
      toast.error('Failed to load exam dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSchoolChange = (schoolId) => {
    setSelectedSchool(schoolId);
    fetchData(schoolId || null);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-48" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <div key={i} className="skeleton h-32 rounded-lg" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="exam-dashboard">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Award className="w-6 h-6" />
          Exam Dashboard
          {data?.overview?.school_name && (
            <span className="text-lg font-normal text-muted-foreground">- {data.overview.school_name}</span>
          )}
        </h1>
        {user?.role === 'super_admin' && (
          <Select value={selectedSchool || "all"} onValueChange={(v) => handleSchoolChange(v === "all" ? "" : v)}>
            <SelectTrigger className="w-64" data-testid="exam-school-filter">
              <SelectValue placeholder="All Schools" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Schools</SelectItem>
              {schools.map(school => (
                <SelectItem key={school.school_id} value={school.school_id}>{school.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Overview Stats */}
      {data?.overview && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="stat-card bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20">
            <Users className="w-8 h-8 text-blue-500 mb-2" />
            <div className="text-3xl font-bold">{data.overview.total_students}</div>
            <div className="text-sm text-muted-foreground">Students</div>
          </div>
          <div className="stat-card bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/20">
            <FileText className="w-8 h-8 text-purple-500 mb-2" />
            <div className="text-3xl font-bold">{data.overview.total_attempts}</div>
            <div className="text-sm text-muted-foreground">Exam Attempts</div>
          </div>
          <div className="stat-card bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/20">
            <CheckCircle2 className="w-8 h-8 text-green-500 mb-2" />
            <div className="text-3xl font-bold">{data.overview.passed_count}</div>
            <div className="text-sm text-muted-foreground">Passed</div>
          </div>
          <div className="stat-card bg-gradient-to-br from-red-500/10 to-red-600/5 border border-red-500/20">
            <XCircle className="w-8 h-8 text-red-500 mb-2" />
            <div className="text-3xl font-bold">{data.overview.failed_count}</div>
            <div className="text-sm text-muted-foreground">Failed</div>
          </div>
          <div className="stat-card bg-gradient-to-br from-amber-500/10 to-amber-600/5 border border-amber-500/20">
            <Zap className="w-8 h-8 text-amber-500 mb-2" />
            <div className="text-3xl font-bold">{data.overview.pass_rate}%</div>
            <div className="text-sm text-muted-foreground">Pass Rate</div>
          </div>
        </div>
      )}

      {/* Average Score */}
      {data?.overview?.avg_score !== undefined && (
        <div className="p-4 rounded-lg bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold">Average Score</h3>
              <p className="text-sm text-muted-foreground">Across all completed exams</p>
            </div>
            <div className="text-4xl font-bold text-primary">{data.overview.avg_score}%</div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Course Performance */}
        {data?.course_performance && data.course_performance.length > 0 && (
          <div className="rounded-lg bg-card border border-border overflow-hidden">
            <div className="p-4 border-b border-border">
              <h3 className="font-semibold flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Course Performance
              </h3>
            </div>
            <div className="max-h-96 overflow-y-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Course</th>
                    <th>Attempts</th>
                    <th>Pass Rate</th>
                    <th>Avg Score</th>
                  </tr>
                </thead>
                <tbody>
                  {data.course_performance.map(course => (
                    <tr key={course.course_id}>
                      <td>
                        <div className="font-medium">{course.title}</div>
                        <Badge variant="outline" className="text-xs">{course.language || course.category?.replace('_', ' ') || 'Course'}</Badge>
                      </td>
                      <td>{course.total_attempts}</td>
                      <td>
                        <span className={`font-medium ${course.pass_rate >= 60 ? 'text-green-600' : 'text-red-600'}`}>
                          {course.pass_rate}%
                        </span>
                      </td>
                      <td>{course.avg_score}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Student Leaderboard */}
        {data?.leaderboard && data.leaderboard.length > 0 && (
          <div className="rounded-lg bg-card border border-border overflow-hidden">
            <div className="p-4 border-b border-border">
              <h3 className="font-semibold flex items-center gap-2">
                <Award className="w-5 h-5" />
                Top Performers
              </h3>
            </div>
            <div className="max-h-96 overflow-y-auto">
              <div className="divide-y divide-border">
                {data.leaderboard.slice(0, 10).map((student, idx) => (
                  <div key={student.user_id} className="p-3 flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      idx === 0 ? 'bg-yellow-500 text-white' :
                      idx === 1 ? 'bg-gray-400 text-white' :
                      idx === 2 ? 'bg-amber-600 text-white' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{student.name}</div>
                      <div className="text-xs text-muted-foreground">{student.email}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-green-600">{student.exams_passed} passed</div>
                      <div className="text-xs text-muted-foreground">Avg: {student.avg_score}%</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recent Activity */}
      {data?.recent_activity && data.recent_activity.length > 0 && (
        <div className="rounded-lg bg-card border border-border overflow-hidden">
          <div className="p-4 border-b border-border">
            <h3 className="font-semibold flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Recent Exam Activity
            </h3>
          </div>
          <div className="max-h-64 overflow-y-auto divide-y divide-border">
            {data.recent_activity.map((activity, idx) => (
              <div key={idx} className="p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {activity.completed ? (
                    activity.passed ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )
                  ) : (
                    <Clock className="w-5 h-5 text-amber-500" />
                  )}
                  <div>
                    <span className="font-medium">{activity.student_name}</span>
                    <span className="text-muted-foreground"> took </span>
                    <span className="font-medium">{activity.course_title}</span>
                  </div>
                </div>
                <div className="text-right">
                  {activity.completed ? (
                    <Badge variant={activity.passed ? 'default' : 'destructive'}>
                      {activity.score?.toFixed(0)}%
                    </Badge>
                  ) : (
                    <Badge variant="outline">In Progress</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// System Panel Component (Super Admin Only)
